import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const HeatPumpTypesSection = () => {
  const [activeType, setActiveType] = useState(0);

  const heatPumpTypes = [
    {
      id: 'hybrid',
      name: 'Hybride warmtepomp',
      icon: 'Layers',
      description: 'Combineert een warmtepomp met uw bestaande cv-ketel voor optimale efficiëntie.',
      suitability: 'Ideaal voor bestaande woningen met radiatoren',
      pros: ['Lagere investering', 'Geen aanpassing radiatoren nodig', 'Geleidelijke overstap'],
      cons: ['Nog deels afhankelijk van gas', 'Lagere besparing dan all-electric'],
      image: 'https://images.pexels.com/photos/8853502/pexels-photo-8853502.jpeg?auto=compress&cs=tinysrgb&w=800',
      altText: 'Hybride warmtepomp systeem met cv-ketel combinatie',
      investment: '€4.000 - €7.000',
      savings: '40-50% gasverbruik',
      color: 'blue'
    },
    {
      id: 'allelectric',
      name: 'All-electric warmtepomp',
      icon: 'Zap',
      description: 'Volledig elektrische verwarming zonder gas voor maximale duurzaamheid.',
      suitability: 'Voor goed geïsoleerde woningen met vloerverwarming of LT-radiatoren',
      pros: ['100% gasvrij', 'Maximale besparing', 'Toekomstbestendig'],
      cons: ['Hogere investering', 'Vereist goede isolatie', 'Mogelijk aanpassing afgiftesysteem'],
      image: 'https://images.pexels.com/photos/8853461/pexels-photo-8853461.jpeg?auto=compress&cs=tinysrgb&w=800',
      altText: 'All-electric warmtepomp binnen- en buitenunit installatie',
      investment: '€10.000 - €18.000',
      savings: '70-80% energiekosten',
      color: 'green'
    },
    {
      id: 'ground',
      name: 'Bodemgebonden warmtepomp',
      icon: 'Layers',
      description: 'Haalt warmte uit de bodem via ondergrondse leidingen voor hoogste rendement.',
      suitability: 'Voor woningen met voldoende grond en bij nieuwbouw',
      pros: ['Hoogste rendement (COP 5+)', 'Geen zichtbare buitenunit', 'Zeer stil'],
      cons: ['Hoge aanlegkosten', 'Vereist grondwerk', 'Niet overal mogelijk'],
      image: 'https://images.pexels.com/photos/5691622/pexels-photo-5691622.jpeg?auto=compress&cs=tinysrgb&w=800',
      altText: 'Bodemgebonden warmtepomp met ondergrondse leidingen installatie',
      investment: '€15.000 - €25.000',
      savings: '80-90% energiekosten',
      color: 'orange'
    }
  ];

  const colorMap = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-600', activeBg: 'bg-blue-600' },
    green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-600', activeBg: 'bg-green-600' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-600', activeBg: 'bg-orange-600' }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Soorten warmtepompen
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Kies de warmtepomp die het beste past bij uw woning en situatie.
          </p>
        </motion.div>

        {/* Type Selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {heatPumpTypes?.map((type, index) => {
            const colors = colorMap?.[type?.color];
            const isActive = activeType === index;
            return (
              <motion.button
                key={type?.id}
                onClick={() => setActiveType(index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full border-2 transition-all ${
                  isActive
                    ? `${colors?.activeBg} text-white border-transparent shadow-elevation-2`
                    : `bg-white ${colors?.text} ${colors?.border} hover:${colors?.bg}`
                }`}
              >
                <Icon name={type?.icon} size={20} />
                <span className="font-semibold">{type?.name}</span>
              </motion.button>
            );
          })}
        </div>

        {/* Active Type Details */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeType}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start"
          >
            {/* Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-elevation-3">
              <Image
                src={heatPumpTypes?.[activeType]?.image}
                alt={heatPumpTypes?.[activeType]?.altText}
                className="w-full h-auto"
              />
            </div>

            {/* Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-3">
                  {heatPumpTypes?.[activeType]?.name}
                </h3>
                <p className="text-lg text-muted-foreground">
                  {heatPumpTypes?.[activeType]?.description}
                </p>
              </div>

              {/* Suitability */}
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-blue-900 mb-1">Geschikt voor</p>
                    <p className="text-sm text-blue-700">{heatPumpTypes?.[activeType]?.suitability}</p>
                  </div>
                </div>
              </div>

              {/* Investment & Savings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Investering</p>
                  <p className="text-lg font-bold text-foreground">{heatPumpTypes?.[activeType]?.investment}</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Besparing</p>
                  <p className="text-lg font-bold text-success">{heatPumpTypes?.[activeType]?.savings}</p>
                </div>
              </div>

              {/* Pros & Cons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Pros */}
                <div>
                  <p className="font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="ThumbsUp" size={18} className="text-green-600 mr-2" />
                    Voordelen
                  </p>
                  <div className="space-y-2">
                    {heatPumpTypes?.[activeType]?.pros?.map((pro, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="Check" size={12} className="text-green-600" />
                        </div>
                        <p className="text-sm text-muted-foreground">{pro}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cons */}
                <div>
                  <p className="font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="AlertCircle" size={18} className="text-orange-600 mr-2" />
                    Aandachtspunten
                  </p>
                  <div className="space-y-2">
                    {heatPumpTypes?.[activeType]?.cons?.map((con, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="Minus" size={12} className="text-orange-600" />
                        </div>
                        <p className="text-sm text-muted-foreground">{con}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HeatPumpTypesSection;